import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createCourse,
	deleteCourse,
	fetchCourses,
	fetchCoursesByLevel,
	updateCourse,
} from "../api/course.api";
import { CoursePayload } from "../types/course.type";

export const COURSE_QUERY_KEY = ["courses-list"];

export const useCourses = (levelId?: number | string) => {
	const { data, isLoading, error } = useQuery({
		queryKey: [...COURSE_QUERY_KEY, levelId || "all"],
		queryFn: () => fetchCourses(levelId),
	});

	return {
		courses: data || [],
		isLoading,
		error,
	};
};

export const useCoursesByLevel = (levelId: number | string) => {
	const { data, isLoading, error } = useQuery({
		queryKey: ["courses-by-level", levelId],
		queryFn: () => fetchCoursesByLevel(levelId),
		enabled: Boolean(levelId),
	});

	return {
		courses: data || [],
		isLoading,
		error,
	};
};

export const useCreateCourse = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: CoursePayload) => createCourse(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: COURSE_QUERY_KEY });
		},
	});
};

export const useUpdateCourse = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: number | string;
			payload: CoursePayload;
		}) => updateCourse(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: COURSE_QUERY_KEY });
		},
	});
};

export const useDeleteCourse = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number | string) => deleteCourse(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: COURSE_QUERY_KEY });
		},
	});
};
